import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useUserRolePermissions } from 'hooks';
import DrawerLayoutWrapper from 'common/drawer-layout-wrapper/DrawerLayoutWrapper';
import { format } from 'date-fns';
import { useViewDriverQuery } from './driverStatusEndpoints';

const ViewDriverDrawer = ({ driver, location, onClose, onRemove }) => {
  const { isDispatchingManage } = useUserRolePermissions();
  const { data: driverQuery = {}, isLoading } = useViewDriverQuery({ driverId: driver.driverId });
  const {
    firstName,
    lastName,
    isActive,
    badgeIds = [],
    loadCapabilities,
    accessLevels = [],
  } = driverQuery;

  const statusBoxStyles = {
    alignItems: 'center',
    backgroundColor: isActive ? 'success.light' : 'graphic.light',
    borderRadius: 0.5,
    display: 'flex',
    gridColumn: '1/3',
    height: 20,
    justifyContent: 'center',
    mb: 2,
    width: 54,
  };

  const handleRemove = () => {
    onRemove(driver, location);
  };

  const effectiveDate = driverQuery.certificationDate
    ? format(new Date(driverQuery.certificationDate), 'MMMM dd, yyyy')
    : '';
  const expirationDate = driverQuery.expirationDate
    ? format(new Date(driverQuery.expirationDate), 'MMMM dd, yyyy')
    : '';

  const certifiedThrough = R.or(
    R.isNil(driverQuery.certificationDate),
    R.isNil(driverQuery.expirationDate)
  )
    ? '--'
    : `${effectiveDate} - ${expirationDate}`;

  const buttons = isDispatchingManage ? (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
      <Button onClick={handleRemove} sx={{ width: 160 }}>
        Remove Driver
      </Button>
    </Box>
  ) : null;

  return (
    <Drawer anchor="right" onClose={onClose} open>
      <DrawerLayoutWrapper
        buttons={buttons}
        headerButtonText="CLOSE"
        isLoading={isLoading}
        onHeaderButtonClick={onClose}
        title="View Driver"
      >
        <Stack sx={{ p: 3, width: 390 }}>
          <Typography gutterBottom variant="caption">
            Name
          </Typography>
          <Typography gutterBottom variant="body1bold">
            {lastName}, {firstName}
          </Typography>
          <Box sx={statusBoxStyles}>
            <Typography variant="caption">{isActive ? 'Active' : 'Inactive'}</Typography>
          </Box>
          <Typography gutterBottom variant="caption">
            Badge ID
          </Typography>
          <Typography sx={{ mb: 2 }} variant="body1bold">
            {badgeIds.map(badgeId => (
              <div key={badgeId}>{badgeId}</div>
            ))}
          </Typography>
          <Typography gutterBottom variant="caption">
            Certified Through
          </Typography>
          <Typography sx={{ mb: 2 }} variant="body1bold">
            {certifiedThrough}
          </Typography>
          <Typography gutterBottom variant="caption">
            Location
          </Typography>
          <Typography sx={{ mb: 2 }} variant="body1bold">
            {location.locationId} {location.name}
          </Typography>

          <Divider sx={{ mb: 3, mt: 1 }} />

          <Typography gutterBottom variant="caption">
            Loading Capabilities
          </Typography>
          <Typography sx={{ mb: 2 }} variant="body1bold">
            {loadCapabilities}
          </Typography>

          <Typography gutterBottom variant="caption">
            Access Levels
          </Typography>
          {accessLevels.map(access => (
            <Typography gutterBottom key={access} variant="body1bold">
              {access}
            </Typography>
          ))}
        </Stack>
      </DrawerLayoutWrapper>
    </Drawer>
  );
};

ViewDriverDrawer.propTypes = {
  driver: PropTypes.shape({
    driverId: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape({
    locationId: PropTypes.number,
    name: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ViewDriverDrawer;
